import cc from "classcat"
import numbro from "numbro"
import React, { useEffect, useState } from "react"

import styles from "./ProjectBudgetForm.module.css"

import currency, { formatCurrency } from "~/helpers/CurrencyHelper"
import { dashify } from "~/helpers/general-helpers"

import CurrencyInput from "~/common/CurrencyInput"
import Duration from "~/common/Duration"
import NumericInput from "~/common/NumericInput"
import { Delete } from "~/common/react-icons"

import { calculateRoleBudgetFromMinutesAndRate } from "~/helpers/project-helpers"
import { useProjectBudgetFormContext } from "./ProjectBudgetFormProvider"
import Column from "./TableColumn"
import Row from "./TableRow"

type RoleData = {
  id: number
  name: string
  estimatedMinutes: number
}

type Props = {
  role: RoleData
  rateCardRate: number
  defaultFullTimeMinutes: number
  canDelete?: boolean
  deleteTooltip?: string
  className?: string
  onMinutesChange: (minutes: number) => void
  onRemove: () => void
  effectiveRate?: number
}

const RoleTableRow: React.FC<Props> = (props) => {
  const {
    role,
    rateCardRate,
    defaultFullTimeMinutes,
    canDelete = false,
    deleteTooltip,
    className,
    onMinutesChange,
    onRemove,
    effectiveRate: propEffectiveRate,
  } = props

  const { hideFinancials, showFinancialColumns, showDayView } =
    useProjectBudgetFormContext()

  const hoursPerDay = defaultFullTimeMinutes / 60
  const effectiveRate = propEffectiveRate ?? rateCardRate

  const [days, setDays] = useState(
    role.estimatedMinutes / defaultFullTimeMinutes,
  )

  const budgetFromEstimatedMinutes = numbro(
    calculateRoleBudgetFromMinutesAndRate(role.estimatedMinutes, effectiveRate),
  ).format({ thousandSeparated: true, mantissa: 0 })

  const [budgetInput, setBudgetInput] = useState(budgetFromEstimatedMinutes)

  const disableBudget = hideFinancials === true || !Number(effectiveRate)

  useEffect(() => {
    const rate = Number(effectiveRate)
    if (Number.isNaN(rate) || !effectiveRate) {
      return
    }

    setBudgetInput(budgetFromEstimatedMinutes)
  }, [effectiveRate, budgetFromEstimatedMinutes])

  useEffect(() => {
    setBudgetInput(budgetFromEstimatedMinutes)
    setDays(role.estimatedMinutes / defaultFullTimeMinutes)
  }, [
    role.estimatedMinutes,
    budgetFromEstimatedMinutes,
    defaultFullTimeMinutes,
  ])

  const handleMinutes = (minutes: number) => {
    onMinutesChange(minutes)
  }

  const handleBudget = (e) => {
    const budget = Number(numbro.unformat(e.target.value))

    if (Number.isNaN(budget)) {
      return setBudgetInput(budgetInput)
    }

    const minutes = Math.round((budget / Number(effectiveRate)) * 60)
    handleMinutes(minutes)
  }

  const formatBudgetInput = (val) => {
    if (!val) {
      return setBudgetInput("0")
    }
    if (!Number(val)) {
      return setBudgetInput(budgetInput)
    }

    const value = numbro(val).format({ thousandSeparated: true, mantissa: 0 })
    setBudgetInput(value)
  }

  const handleRemoveRole = () => {
    if (!canDelete) return
    onRemove()
  }

  const formattedRoleName = dashify(role.name)

  return (
    <Row dataTest={`${formattedRoleName}-role-row`} className={className}>
      <Column className={className}>{role.name}</Column>
      {showFinancialColumns && (
        <Column>
          <span data-test={`${formattedRoleName}-project-rate`}>
            {formatCurrency(
              showDayView ? effectiveRate * hoursPerDay : effectiveRate,
            )}
          </span>
        </Column>
      )}
      <Column>
        {showDayView ? (
          <NumericInput
            value={days || 0}
            mantissa={2}
            min={0}
            trimMantissa
            onValueChange={(e) => setDays(e)}
            onBlur={() =>
              handleMinutes(Math.round(days * defaultFullTimeMinutes))
            }
            dataTest={`${formattedRoleName}-budget_days`}
          />
        ) : (
          <Duration
            minutesPerDay={role.estimatedMinutes || null}
            handleMinutesPerDay={handleMinutes}
            allowZero
            defaultDuration={0}
            dataTest={`${formattedRoleName}-budget_hours`}
            height={30}
            style={{
              borderColor: "var(--winter)",
            }}
          />
        )}
      </Column>
      {showFinancialColumns && (
        <Column>
          <CurrencyInput
            name={`${formattedRoleName}-budget_dollar`}
            dataTest={`${formattedRoleName}-budget_dollar`}
            value={budgetInput}
            onChange={formatBudgetInput}
            onBlur={handleBudget}
            currency={currency}
            disabled={disableBudget}
            height={30}
            selectAllOnFocus
          />
        </Column>
      )}
      <div
        className={cc([
          styles.deleteIconContainer,
          !canDelete && styles.disabled,
        ])}
        onClick={handleRemoveRole}
        data-test={`${formattedRoleName}-role-delete`}
      >
        <Delete alt={deleteTooltip} color="var(--winter)" />
      </div>
    </Row>
  )
}

export default RoleTableRow
